import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Heading,
  SimpleGrid,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Image,
  IconButton,
  VStack,
  HStack,
  Text,
  Center,
  Spinner,
  useColorModeValue,
} from '@chakra-ui/react';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { categories_url } from '../utils/constants';
import { getSecureUrl } from '../utils/helpers';
import { SidebarWithHeader } from '../components';

const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryName, setCategoryName] = useState('');
  const [categoryImage, setCategoryImage] = useState('');
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cardBg = useColorModeValue('white', 'gray.700');

  const fetchCategories = async () => {
    try {
      const response = await axios.get(categories_url);
      const cats = (response.data.data || []).map((cat) => ({
        ...cat,
        image: cat.image ? { ...cat.image, url: getSecureUrl(cat.image.url) } : cat.image,
      }));
      setCategories(cats);
    } catch (error) {
      toast({
        position: 'top',
        description: 'Error fetching categories',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line
  }, []);

  const onDrop = useCallback((acceptedFiles) => {
    acceptedFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        setCategoryImage(reader.result);
      };
      reader.readAsDataURL(file);
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'image/jpeg, image/png',
    multiple: false,
  });

  const handleOpenModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryName(category.name);
      setCategoryImage(category.image.url);
    } else {
      setEditingCategory(null);
      setCategoryName('');
      setCategoryImage('');
    }
    onOpen();
  };

  const handleCloseModal = () => {
    setEditingCategory(null);
    setCategoryName('');
    setCategoryImage('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!categoryName || !categoryImage) {
      return toast({
        position: 'top',
        description: 'Please provide category name and image',
        status: 'error',
        duration: 5000,
      });
    }

    setSaving(true);
    try {
      if (editingCategory) {
        // Update existing category
        await axios.put(
          `${categories_url}/admin/${editingCategory._id}`,
          {
            name: categoryName,
            image: categoryImage.startsWith('data:') ? categoryImage : undefined,
          },
          {
            headers: {
              Authorization: 'Bearer ' + localStorage.getItem('acces_token'),
            },
          }
        );
        toast({
          position: 'top',
          description: 'Category updated successfully',
          status: 'success',
          duration: 5000,
        });
      } else {
        // Create new category
        await axios.post(
          `${categories_url}/admin`,
          { name: categoryName, image: categoryImage },
          {
            headers: {
              Authorization: 'Bearer ' + localStorage.getItem('acces_token'),
            },
          }
        );
        toast({
          position: 'top',
          description: 'Category created successfully',
          status: 'success',
          duration: 5000,
        });
      }
      fetchCategories();
      handleCloseModal();
    } catch (error) {
      toast({
        position: 'top',
        description: error.response?.data?.message || 'Error saving category',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;

    try {
      await axios.delete(`${categories_url}/admin/${id}`, {
        headers: {
          Authorization: 'Bearer ' + localStorage.getItem('acces_token'),
        },
      });
      toast({
        position: 'top',
        description: 'Category deleted successfully',
        status: 'success',
        duration: 5000,
      });
      fetchCategories();
    } catch (error) {
      toast({
        position: 'top',
        description: error.response?.data?.message || 'Error deleting category',
        status: 'error',
        duration: 5000,
      });
    }
  };

  if (loading) {
    return (
      <SidebarWithHeader>
        <Center h="400px">
          <Spinner size="xl" color="brown.500" />
        </Center>
      </SidebarWithHeader>
    );
  }

  return (
    <SidebarWithHeader>
      <Box p={4}>
        <HStack justify="space-between" mb={6}>
          <Heading size="lg">Categories</Heading>
          <Button colorScheme="brown" leftIcon={<FaPlus />} onClick={() => handleOpenModal()}>
            Add Category
          </Button>
        </HStack>

        <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={6}>
          {categories.map((category) => (
            <Box
              key={category._id}
              bg={cardBg}
              borderRadius="lg"
              overflow="hidden"
              boxShadow="md"
              position="relative"
            >
              <Image
                src={category.image.url}
                alt={category.name}
                h="200px"
                w="100%"
                objectFit="cover"
              />
              <Box p={4}>
                <Text fontWeight="bold" textTransform="capitalize" fontSize="lg">
                  {category.name}
                </Text>
                <HStack mt={3} spacing={2}>
                  <IconButton
                    size="sm"
                    colorScheme="blue"
                    icon={<FaEdit />}
                    onClick={() => handleOpenModal(category)}
                    aria-label="Edit"
                  />
                  <IconButton
                    size="sm"
                    colorScheme="red"
                    icon={<FaTrash />}
                    onClick={() => handleDelete(category._id)}
                    aria-label="Delete"
                  />
                </HStack>
              </Box>
            </Box>
          ))}
        </SimpleGrid>

        {categories.length === 0 && (
          <Center h="200px">
            <VStack>
              <Text color="gray.500">No categories found</Text>
              <Button colorScheme="brown" onClick={() => handleOpenModal()}>
                Add your first category
              </Button>
            </VStack>
          </Center>
        )}

        {/* Add/Edit Category Modal */}
        <Modal isOpen={isOpen} onClose={handleCloseModal}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{editingCategory ? 'Edit Category' : 'Add New Category'}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4}>
                <FormControl>
                  <FormLabel>Category Name</FormLabel>
                  <Input
                    placeholder="Enter category name"
                    value={categoryName}
                    onChange={(e) => setCategoryName(e.target.value)}
                    focusBorderColor="brown.500"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Category Image</FormLabel>
                  <Center
                    bg="brown.50"
                    minHeight={150}
                    borderWidth={3}
                    borderColor="brown.200"
                    borderStyle="dashed"
                    borderRadius="lg"
                    cursor="pointer"
                    {...getRootProps()}
                  >
                    {categoryImage ? (
                      <Image
                        src={categoryImage}
                        alt="Category"
                        maxH="140px"
                        objectFit="contain"
                      />
                    ) : isDragActive ? (
                      <Text>Drop the image here</Text>
                    ) : (
                      <Text textAlign="center" p={4}>
                        Drag & drop an image here, or click to select
                      </Text>
                    )}
                  </Center>
                  <Input {...getInputProps()} />
                </FormControl>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button mr={3} onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button
                colorScheme="brown"
                onClick={handleSubmit}
                isLoading={saving}
                loadingText="Saving..."
              >
                {editingCategory ? 'Update' : 'Create'}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </SidebarWithHeader>
  );
};

export default CategoriesPage;
